import React, { useState, useMemo, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import { FiX, FiMusic, FiSearch, FiChevronDown, FiFilter, FiLink, FiMail, FiCheck } from 'react-icons/fi';
import { musicData } from '../data/music';
import { supabase } from '../lib/supabase';

// Alle Titel (Demos + Konzepte) kommen aus Supabase. Diese Funktion bildet
// einen DB-Datensatz auf das Format ab, das die Seite erwartet.
// textAuszug ergänzt das DB-Feld text_auszug (Konzept-Textauszüge).
const mapMusicRow = (m) => ({
  ...m,
  _key: `db-${m.id}`,
  _image: m.image,
  textAuszug: m.text_auszug ?? null,
});

// Fallback aus der lokalen Datei, falls Supabase nicht erreichbar ist.
const fallbackMusicItems = musicData.map(mapMusicRow);

const uniqueSorted = (arr, fixed = []) => {
  const fromData = Array.from(new Set(arr.filter(Boolean))).sort();
  const extra = fixed.filter((f) => !fromData.includes(f));
  return ['Alle', ...fromData, ...extra];
};

const ImagePlaceholder = () => (
  <div className="w-full h-full bg-brand dark:bg-navy-800 flex items-center justify-center">
    <SafeIcon icon={FiMusic} className="w-16 h-16 text-white/40" />
  </div>
);

const FilterDropdown = ({ label, options, value, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const active = value !== 'Alle';

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${
          active
            ? 'bg-brand text-white border-brand dark:bg-amber-600 dark:border-amber-600'
            : 'bg-white dark:bg-navy-800 text-stone-600 dark:text-stone-300 border-stone-200 dark:border-navy-700 hover:bg-stone-100 dark:hover:bg-navy-700'
        }`}
      >
        <span>{label}{active ? `: ${value}` : ''}</span>
        <SafeIcon icon={FiChevronDown} className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.15 }}
            className="absolute top-full mt-2 left-0 z-30 min-w-[160px] bg-white dark:bg-navy-800 border border-stone-200 dark:border-navy-700 rounded-sm shadow-lg overflow-hidden"
          >
            {options.map((opt) => (
              <button
                key={opt}
                onClick={() => { onChange(opt); setOpen(false); }}
                className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
                  value === opt
                    ? 'bg-brand/10 dark:bg-amber-600/20 text-brand dark:text-amber-400 font-semibold'
                    : 'text-stone-700 dark:text-stone-300 hover:bg-stone-50 dark:hover:bg-navy-700'
                }`}
              >
                {opt}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const MusicPage = () => {
  const [search, setSearch] = useState('');
  const [filterLang, setFilterLang] = useState('Alle');
  const [filterPoet, setFilterPoet] = useState('Alle');
  const [filterGenre, setFilterGenre] = useState('Alle');
  const [filterYear, setFilterYear] = useState('Alle');
  const [filterVideo, setFilterVideo] = useState('Alle');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [copied, setCopied] = useState(false);
  const [searchParams] = useSearchParams();
  const { id: liedId } = useParams();
  const navigate = useNavigate();

  // Musik-Demos: zuerst Fallback aus der Datei, dann (sobald geladen) aus Supabase.
  const [musicItems, setMusicItems] = useState(fallbackMusicItems);

  useEffect(() => {
    if (!supabase) return; // keine Verbindung konfiguriert → Fallback bleibt
    let aktiv = true;
    supabase
      .from('music')
      .select('*')
      .order('sort_order', { ascending: true })
      .then(({ data, error }) => {
        if (!aktiv) return;
        if (error) {
          console.warn('Supabase-Laden fehlgeschlagen, nutze Fallback:', error.message);
          return;
        }
        if (data && data.length > 0) {
          setMusicItems(data.map(mapMusicRow));
        }
      });
    return () => { aktiv = false; };
  }, []);

  const allItems = musicItems;

  // Ein geöffneter Titel steht als /lied/<id> in der Adresszeile. Diese URL
  // liefert Link-Vorschauen mit Cover (nginx → Edge Function), lässt sich also
  // direkt aus der Adresszeile teilen. Das alte ?titel=<id> wird umgeschrieben.
  const titelParam = searchParams.get('titel');
  useEffect(() => {
    if (titelParam) navigate(`/lied/${titelParam}`, { replace: true });
  }, [titelParam, navigate]);

  // Titel aus der URL öffnen (auch nach Zurück/Vor im Browser).
  useEffect(() => {
    if (!liedId) {
      setSelected(null);
      return;
    }
    const found = allItems.find((i) => String(i.id) === String(liedId));
    if (found) setSelected(found);
  }, [allItems, liedId]);

  const openTitle = (item) => {
    setSelected(item);
    if (item?.id != null) navigate(`/lied/${item.id}`);
  };

  const closeTitle = () => {
    setSelected(null);
    setCopied(false);
    if (liedId) navigate('/musik', { replace: true });
  };

  // Empfehl-Link zum aktuell geöffneten Titel. /lied/<id> leitet Bots zur
  // Edge Function (Cover-Vorschau) und Menschen ins Titel-Modal.
  const shareUrl = selected ? `https://www.liedzeit.de/lied/${selected.id}` : '';

  const copyShareLink = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title: `Liedzeit: ${selected.title}`, url: shareUrl });
        return;
      }
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      // Nutzer hat Teilen/Kopieren abgebrochen – kein Fehler nötig.
    }
  };

  const mailLink = selected
    ? `mailto:?subject=${encodeURIComponent(`Liedzeit: ${selected.title}`)}&body=${encodeURIComponent(
        `Ich möchte dir diesen Titel empfehlen:\n\n${selected.title}${selected.poet ? ' – ' + selected.poet : ''}\n\n${shareUrl}`
      )}`
    : '';

  const languages = useMemo(() => uniqueSorted(allItems.map((i) => i.language), ['Englisch']), [allItems]);
  const poets     = useMemo(() => uniqueSorted(allItems.map((i) => i.poet)), [allItems]);
  const genres    = useMemo(() => uniqueSorted(allItems.map((i) => i.style)), [allItems]);
  const years     = useMemo(() => uniqueSorted(allItems.map((i) => i.year)), [allItems]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allItems.filter((item) => {
      if (filterLang !== 'Alle' && item.language !== filterLang) return false;
      if (filterPoet !== 'Alle' && item.poet !== filterPoet) return false;
      if (filterGenre !== 'Alle' && item.style !== filterGenre) return false;
      if (filterYear !== 'Alle' && item.year !== filterYear) return false;
      if (filterVideo === 'Mit Video' && !item.video) return false;
      if (filterVideo === 'Ohne Video' && item.video) return false;
      if (q && !`${item.title} ${item.subtitle ?? ''} ${item.poet} ${item.description ?? ''}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [allItems, search, filterLang, filterPoet, filterGenre, filterYear, filterVideo]);

  const activeCount = [filterLang, filterPoet, filterGenre, filterYear, filterVideo].filter((v) => v !== 'Alle').length + (search ? 1 : 0);

  const resetFilters = () => {
    setSearch('');
    setFilterLang('Alle');
    setFilterPoet('Alle');
    setFilterGenre('Alle');
    setFilterYear('Alle');
    setFilterVideo('Alle');
  };

  return (
    <div className="bg-white dark:bg-navy-950 min-h-screen transition-colors duration-300">
      {/* Header */}
      <section className="bg-brand dark:bg-navy-900 text-white py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-xs font-bold tracking-widest text-amber-300 uppercase mb-3">
              Liedzeit
            </p>
            <h1 className="text-4xl md:text-6xl font-serif font-bold mb-4">
              Musik & Song-Konzepte
            </h1>
            <p className="text-stone-300 max-w-2xl text-lg font-light">
              Hörbare Demos und textliche Konzepte – Lyrik als Ausgangspunkt für Klang.
              Klicken Sie auf einen Titel für Details.
            </p>
            <a
              href="https://radio.liedzeit.de/public/radio_liedzeit"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block mt-8 bg-amber-700 hover:bg-amber-800 text-white px-8 py-3 rounded-sm text-lg transition-all duration-300 shadow-lg"
            >
              Radio Liedzeit
            </a>
          </motion.div>
        </div>
      </section>

      {/* Filter */}
      <section className="border-b border-stone-100 dark:border-navy-800 bg-stone-50 dark:bg-navy-900 py-4 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Kopfzeile: Suche + Filter-Toggle */}
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[200px] max-w-sm">
              <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400 pointer-events-none" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Titel, Texter, Beschreibung …"
                className="w-full pl-9 pr-4 py-2 text-sm rounded-full border border-stone-200 dark:border-navy-700 bg-white dark:bg-navy-800 text-stone-700 dark:text-stone-300 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-brand/40"
              />
            </div>

            <button
              onClick={() => setFiltersOpen((o) => !o)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${
                activeCount > 0
                  ? 'bg-brand text-white border-brand dark:bg-amber-600 dark:border-amber-600'
                  : 'bg-white dark:bg-navy-800 text-stone-600 dark:text-stone-300 border-stone-200 dark:border-navy-700 hover:bg-stone-100 dark:hover:bg-navy-700'
              }`}
            >
              <SafeIcon icon={FiFilter} className="w-3.5 h-3.5" />
              <span>Filter{activeCount > 0 ? ` (${activeCount})` : ''}</span>
              <SafeIcon icon={FiChevronDown} className={`w-3.5 h-3.5 transition-transform ${filtersOpen ? 'rotate-180' : ''}`} />
            </button>

            {activeCount > 0 && (
              <button onClick={resetFilters} className="text-xs text-amber-700 dark:text-amber-400 hover:underline font-semibold">
                Zurücksetzen
              </button>
            )}
          </div>

          {/* Ausgeklappte Dropdowns */}
          <AnimatePresence>
            {filtersOpen && (
              <motion.div
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.15 }}
              >
                <div className="flex gap-3 flex-wrap pt-4 pb-2">
                  <FilterDropdown label="Sprache" options={languages}                         value={filterLang}  onChange={setFilterLang} />
                  <FilterDropdown label="Texter"  options={poets}                           value={filterPoet}  onChange={setFilterPoet} />
                  <FilterDropdown label="Genre"   options={genres}                          value={filterGenre} onChange={setFilterGenre} />
                  <FilterDropdown label="Jahr"    options={years}                           value={filterYear}  onChange={setFilterYear} />
                  <FilterDropdown label="Video"   options={['Alle', 'Mit Video', 'Ohne Video']} value={filterVideo} onChange={setFilterVideo} />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>

      {/* Grid */}
      <section className="py-16 px-4">
        <div className="max-w-7xl mx-auto">
          {filtered.length === 0 && (
            <div className="text-center py-24 text-stone-400 dark:text-stone-600">
              <SafeIcon icon={FiMusic} className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p className="text-lg font-serif">Keine Ergebnisse gefunden.</p>
              <button onClick={resetFilters} className="mt-4 text-sm text-amber-700 dark:text-amber-400 hover:underline">
                Filter zurücksetzen
              </button>
            </div>
          )}
        </div>
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filtered.map((item, i) => (
            <div
              key={item._key}
              className="group cursor-pointer bg-white dark:bg-navy-900 rounded-sm shadow-sm hover:shadow-lg transition-shadow duration-300 overflow-hidden"
              onClick={() => openTitle(item)}
            >
              <div className="aspect-[4/3] overflow-hidden">
                {item._image ? (
                  <img
                    src={item._image}
                    alt={item.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                ) : (
                  <ImagePlaceholder />
                )}
              </div>
              <div className="p-6">
                <p className="text-xs font-bold tracking-widest uppercase text-amber-700 dark:text-amber-400 mb-1">
                  {item.poet}
                </p>
                <h3 className="text-xl font-serif font-bold text-stone-900 dark:text-stone-100 mb-1">
                  {item.title}
                </h3>
                {(item.subtitle || item.style) && (
                  <p className="text-stone-500 dark:text-stone-400 text-sm italic mb-2">
                    {item.subtitle || item.style}
                  </p>
                )}
                <p className="text-stone-500 dark:text-stone-400 text-sm line-clamp-2">
                  {item.description}
                </p>
                <div className="flex gap-2 mt-3 flex-wrap">
                  <span className="text-xs bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300 px-2 py-0.5 rounded-full font-semibold">
                    {item.status}
                  </span>
                  {item.audio && (
                    <span className="text-xs bg-stone-100 dark:bg-navy-800 text-stone-600 dark:text-stone-400 px-2 py-0.5 rounded-full">
                      Audio
                    </span>
                  )}
                  {item.video && (
                    <span className="text-xs bg-stone-100 dark:bg-navy-800 text-stone-600 dark:text-stone-400 px-2 py-0.5 rounded-full">
                      Video
                    </span>
                  )}
                  {item.lyrics && (
                    <span className="text-xs bg-stone-100 dark:bg-navy-800 text-stone-600 dark:text-stone-400 px-2 py-0.5 rounded-full">
                      Liedtext
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Modal */}
      <AnimatePresence>
        {selected && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
            onClick={() => closeTitle()}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.25 }}
              className="bg-white dark:bg-navy-900 rounded-sm shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex flex-col md:flex-row">
                <div className="w-full md:w-2/5 flex-shrink-0">
                  <div className="aspect-square overflow-hidden">
                    {selected._image ? (
                      <img
                        src={selected._image}
                        alt={selected.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <ImagePlaceholder />
                    )}
                  </div>
                </div>

                <div className="flex-1 p-8 flex flex-col">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <p className="text-xs font-bold tracking-widest uppercase text-amber-700 dark:text-amber-400 mb-1">
                        {selected.poet}
                      </p>
                      <h2 className="text-2xl font-serif font-bold text-stone-900 dark:text-stone-100">
                        {selected.title}
                      </h2>
                      {selected.subtitle && (
                        <p className="text-stone-500 dark:text-stone-400 italic text-sm mt-1">
                          {selected.subtitle}
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => closeTitle()}
                      className="text-stone-400 hover:text-stone-700 dark:hover:text-stone-200 transition-colors ml-4 flex-shrink-0"
                      aria-label="Schließen"
                    >
                      <SafeIcon icon={FiX} className="w-5 h-5" />
                    </button>
                  </div>

                  <div className="flex gap-2 mb-4 flex-wrap">
                    <span className="text-xs bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300 px-3 py-1 rounded-full font-semibold">
                      {selected.status}
                    </span>
                    {selected.style && (
                      <span className="text-xs bg-stone-100 dark:bg-navy-800 text-stone-600 dark:text-stone-400 px-3 py-1 rounded-full">
                        {selected.style}
                      </span>
                    )}
                    {selected.language && (
                      <span className="text-xs bg-stone-100 dark:bg-navy-800 text-stone-600 dark:text-stone-400 px-3 py-1 rounded-full">
                        {selected.language}
                      </span>
                    )}
                    <span className="text-xs bg-stone-100 dark:bg-navy-800 text-stone-600 dark:text-stone-400 px-3 py-1 rounded-full">
                      {selected.year}
                    </span>
                  </div>

                  <p className="text-stone-600 dark:text-stone-400 text-sm leading-relaxed mb-5">
                    {selected.description}
                  </p>

                  {selected.textAuszug && (
                    <blockquote className="border-l-2 border-amber-400 pl-4 italic text-stone-700 dark:text-stone-300 text-sm mb-5">
                      {selected.textAuszug}
                    </blockquote>
                  )}

                  {selected.audio && (
                    <div className="mb-4">
                      <p className="text-xs font-bold tracking-widest uppercase text-stone-400 dark:text-stone-500 mb-2">
                        Audio
                      </p>
                      <audio
                        controls
                        className="w-full"
                        style={{ accentColor: '#b45309' }}
                        ref={(el) => { if (el) el.volume = 0.4; }}
                      >
                        <source src={selected.audio} />
                        Ihr Browser unterstützt kein Audio.
                      </audio>
                    </div>
                  )}

                  {selected.video && (
                    <div className="mb-4">
                      <p className="text-xs font-bold tracking-widest uppercase text-stone-400 dark:text-stone-500 mb-2">
                        Video
                      </p>
                      <video
                        controls
                        className="w-full rounded-sm"
                        style={{ accentColor: '#b45309' }}
                        ref={(el) => { if (el) el.volume = 0.4; }}
                      >
                        <source src={selected.video} />
                        Ihr Browser unterstützt kein Video.
                      </video>
                    </div>
                  )}

                  {!selected.audio && (
                    <div className="pt-4">
                      <Link
                        to="/kontakt"
                        onClick={() => closeTitle()}
                        className="inline-block bg-brand dark:bg-brand-light text-white px-6 py-2.5 rounded-sm text-sm font-semibold hover:bg-brand-dark dark:hover:bg-brand transition-colors"
                      >
                        Anfragen / Kontakt
                      </Link>
                    </div>
                  )}

                  {/* Teilen / Empfehlen */}
                  <div className="mt-auto pt-5 flex flex-wrap items-center gap-3">
                    <button
                      onClick={copyShareLink}
                      className="inline-flex items-center gap-2 text-sm font-semibold text-brand dark:text-amber-400 hover:text-brand-dark dark:hover:text-amber-300 transition-colors"
                    >
                      <SafeIcon icon={copied ? FiCheck : FiLink} className="w-4 h-4" />
                      {copied ? 'Link kopiert!' : 'Link kopieren'}
                    </button>
                    <span className="text-stone-300 dark:text-navy-700">·</span>
                    <a
                      href={mailLink}
                      className="inline-flex items-center gap-2 text-sm font-semibold text-brand dark:text-amber-400 hover:text-brand-dark dark:hover:text-amber-300 transition-colors"
                    >
                      <SafeIcon icon={FiMail} className="w-4 h-4" />
                      Per E-Mail empfehlen
                    </a>
                  </div>
                </div>
              </div>

              {selected.lyrics && (
                <div className="px-8 pb-8 border-t border-stone-100 dark:border-navy-800 pt-6">
                  <p className="text-xs font-bold tracking-widest uppercase text-stone-400 dark:text-stone-500 mb-3">
                    Liedtext
                  </p>
                  <pre className="font-serif text-stone-700 dark:text-stone-300 text-sm leading-relaxed whitespace-pre-wrap">
                    {selected.lyrics}
                  </pre>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MusicPage;
